import React, { useEffect, useMemo, useState } from 'react';
import { Link } from '@mui/material';
import { PaymentConnection, Payment, OrderDirection, Maybe, PaymentMethod } from '../gql-types.generated';
import {
  DataGridProProps,
  GridApiRef,
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRowModel,
  GridRowParams,
  GridSelectionModel,
} from '@mui/x-data-grid-pro';
import { mergeSavedColumnOrder } from '../util/GridColumnOrder';
import { getPaymentMethodTypeDisplay } from '../util/PaymentMethodType';
import { paymentStatusDisplay } from '../util/PaymentStatus';
import { PaymentStatusForDisplay } from '../util/PaymentStatusForDisplay';
import { DataList } from './DataList';
import { TruncatedTextWithTooltip } from './TruncatedTextWithTooltip';

// Table for the payments
interface PaymentListProps {
  defaultCurrency?: string | null;
  paymentConnection: PaymentConnection;
  payments: Payment[] | undefined;
  direction: OrderDirection;
  field: string;
  handleSort: (arrowDirection: OrderDirection, fieldName: string) => void;
  loadPage: (endEdge: string) => void;
  selectedRecord?: Payment;
  setSelectedRecord: (record: Payment | undefined) => void;
  handleViewPayment: (record: Payment | undefined) => void;
  isLoadingPayments: boolean;
  isCreditMemoEnabled: boolean;
  isCustomerEnabled: boolean;
  gridApiRef: GridApiRef;
  isConvenienceFeeEnabled: boolean;
}

const PaymentList: React.FC<PaymentListProps> = props => {
  const {
    defaultCurrency,
    paymentConnection,
    payments,
    handleViewPayment,
    loadPage,
    selectedRecord,
    setSelectedRecord,
    isLoadingPayments,
    isCreditMemoEnabled,
    isCustomerEnabled,
    gridApiRef,
    isConvenienceFeeEnabled,
  } = props;

  const hasCreditApplied = !!payments?.find(payment => {
    return !!payment.creditAmount;
  });

  const storageKey = 'paymentsGridColumnOrder';
  const visibilityStorageKey = 'paymentsGridColumnVisibility';

  const savedOrder = useMemo(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return undefined;
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : undefined;
    } catch {
      return undefined;
    }
  }, []);

  const savedVisibilityModel = useMemo(() => {
    try {
      const raw = localStorage.getItem(visibilityStorageKey);
      if (!raw) return undefined;
      const obj = JSON.parse(raw);
      return obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : undefined;
    } catch {
      return undefined;
    }
  }, []);

  const getPaymentMethod = (isCreditPayment: boolean, paymentMethod?: PaymentMethod | null) => {
    if (!isCreditPayment && paymentMethod?.creditCard) {
      return `${paymentMethod.creditCard.cardBrand?.substring(0, 1)}${paymentMethod.creditCard.cardBrand
        ?.substring(1)
        .toLowerCase()} ${paymentMethod.creditCard.lastFour}`;
    }

    if (!isCreditPayment && paymentMethod?.paymentBank) {
      return `Bank ${paymentMethod.paymentBank.lastFour}`;
    }

    return 'None';
  };

  const defaultVisibilityModel: GridColumnVisibilityModel = useMemo(
    () => ({
      customerNumber: false,
      authorizationCode: false,
      captureType: false,
      createdOn: false,
      paidByEmail: false,
      paidByName: false,
      paidOn: false,
      ...savedVisibilityModel,
    }),
    [savedVisibilityModel],
  );

  // Mirrors what's persisted to localStorage so the loading skeleton can render the same
  // columns as the real grid - the grid's own columnVisibilityModel lives in its internal
  // apiRef state and isn't otherwise readable from here.
  const [visibilityModel, setVisibilityModel] = useState<GridColumnVisibilityModel>(defaultVisibilityModel);

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    if (!gridApiRef.current) return;
    const ordered = gridApiRef.current.getAllColumns().map(c => c.field);
    localStorage.setItem(storageKey, JSON.stringify(ordered));
  };

  const handleColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
    localStorage.setItem(visibilityStorageKey, JSON.stringify(model));
    setVisibilityModel(model);
  };

  const getPaymentRows = () => {
    return payments?.map((row: Maybe<Payment>) => {
      const node = row;
      if (!node) {
        return {} as GridRowModel;
      }
      const isSelected = node.id === selectedRecord?.id;
      const isCreditPayment = (node?.creditAmount && node.creditAmount > 0 && (node.amount === 0 || !node?.amount)) as boolean;
      const paymentAmount = node?.amountBeforeFees || node?.amount || 0;
      const convenienceFee = node?.convenienceFee || 0;
      return {
        _raw: node,
        id: node.id,
        isSelected,
        paymentId: node.id?.slice(-9),
        customerName: node?.customer?.name || '',
        customerNumber: node?.customer?.customerNumber || '',
        paymentMethod: getPaymentMethod(isCreditPayment, node?.paymentMethod),
        paymentType: getPaymentMethodTypeDisplay(isCreditPayment, node?.paymentMethod),
        creditAmount: new Intl.NumberFormat('en', {
          style: 'currency',
          currency: node?.currency || defaultCurrency || 'USD',
          currencyDisplay: 'symbol',
        }).format((node.creditAmount || 0) / 100),
        paymentMethodAmount: new Intl.NumberFormat('en', {
          style: 'currency',
          currency: node?.currency || defaultCurrency || 'USD',
          currencyDisplay: 'symbol',
        }).format(paymentAmount / 100),
        convenienceFeeAmount: new Intl.NumberFormat('en', {
          style: 'currency',
          currency: node?.currency || defaultCurrency || 'USD',
          currencyDisplay: 'symbol',
        }).format(convenienceFee / 100),
        totalAmount: new Intl.NumberFormat('en', {
          style: 'currency',
          currency: node?.currency || defaultCurrency || 'USD',
          currencyDisplay: 'symbol',
        }).format(((node.amount || 0) + (node.creditAmount || 0)) / 100),
        customerPoNumber: node?.customerPONumber || '',
        invoiceNumber: node?.invoiceNumber || '',
        orderNumber: node?.orderNumber || '',
        description: node?.description || '',
        authorizationCode: node?.authorizationCode || '',
        captureType: node?.immediateCapture ? 'Immediate' : 'Authorization',
        createdOn: node?.createdAt ? new Date(node.createdAt).toLocaleDateString() : '',
        paidByEmail:
          node?.paymentMethod?.creditCard?.cardHolder?.email || node?.paymentMethod?.paymentBank?.accountHolder?.email || '',
        paidByName:
          node?.paymentMethod?.creditCard?.cardHolder?.holderName || node?.paymentMethod?.paymentBank?.accountHolder?.holderName || '',
        paidOn: node?.completedTimestamp ? new Date(node.completedTimestamp).toLocaleDateString() : '',
        statusRender: PaymentStatusForDisplay(
          paymentStatusDisplay.find(item => {
            return item.name === node.status;
          })?.display,
          node.amountBeforeFees || node.amount || 0,
          node.refunds,
          node.createdAt,
          node.immediateCapture,
          node.pendingReasonCode,
        ),
        date:
          (node.completedTimestamp && new Date(node.completedTimestamp).toLocaleDateString()) ||
          (node.createdAt && new Date(node.createdAt).toLocaleDateString()) ||
          '- -',
      } as GridRowModel;
    }) as GridRowModel[];
  };

  const getPaymentById = (id: string) => {
    const foundPayment = payments?.find(payment => payment?.id === id);
    // eslint-disable-next-line no-underscore-dangle
    return foundPayment;
  };
  const [paymentRows, setPaymentRows] = useState<GridRowModel[]>(getPaymentRows());
  const [loadingPage, setLoadingPage] = useState(false);

  useEffect(() => {
    setLoadingPage(false);
    setPaymentRows(getPaymentRows());
  }, [payments]);

  const handlePageLoad = () => {
    if (!paymentConnection) {
      // There is not connection to use for retrieving the next page
      return;
    }
    // No more pages to load, no need to send the load request
    if (!paymentConnection.pageInfo.hasNextPage) {
      return;
    }
    if (!paymentConnection.pageInfo.endCursor) {
      return;
    }
    setLoadingPage(true);
    loadPage(paymentConnection.pageInfo.endCursor);
  };

  const paymentColumns: GridColDef[] = useMemo(
    () => [
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Link
              underline={'hover'}
              href="#"
              onClick={(event: React.MouseEvent) => {
                // stopPropagation to stop click propagation through to the grid - so it does not toggle checkbox.
                event.stopPropagation();
                // eslint-disable-next-line no-underscore-dangle
                handleViewPayment(params?.row?._raw as Payment);
              }}
            >
              ***{params.value}
            </Link>
          );
        },
        resizable: false,
        field: 'paymentId',
        sortable: false,
        headerName: 'Payment ID',
        flex: 0.16,
        minWidth: 130,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'statusRender',
        sortable: false,
        headerName: 'Status',
        flex: 0.12,
        minWidth: 130,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'paymentMethod',
        sortable: false,
        headerName: 'Payment Method',
        flex: 0.12,
        minWidth: 160,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'paymentType',
        sortable: false,
        headerName: 'Payment Type',
        flex: 0.1,
        minWidth: 130,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'customerName',
        hide: !isCustomerEnabled,
        sortable: false,
        headerName: 'Customer Name',
        flex: 0.12,
        minWidth: 160,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'customerNumber',
        hide: !isCustomerEnabled,
        sortable: false,
        headerName: 'Customer Number',
        flex: 0.12,
        minWidth: 160,
      },
      {
        resizable: false,
        field: 'date',
        sortable: false,
        headerName: 'Last Updated',
        flex: 0.1,
        minWidth: 130,
      },
      {
        resizable: false,
        field: 'creditAmount',
        sortable: false,
        hide: !isCreditMemoEnabled || !hasCreditApplied,
        headerName: 'Credit Applied',
        headerAlign: 'right',
        align: 'right',
        flex: 0.12,
        minWidth: 140,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} align="right" />,
        resizable: false,
        field: 'paymentMethodAmount',
        sortable: false,
        hide: !(isCreditMemoEnabled || isConvenienceFeeEnabled),
        headerName: 'Paid',
        headerAlign: 'right',
        align: 'right',
        flex: 0.12,
        minWidth: 150,
      },
      {
        resizable: false,
        field: 'convenienceFeeAmount',
        sortable: false,
        hide: !isConvenienceFeeEnabled,
        headerName: 'Fees',
        headerAlign: 'right',
        align: 'right',
        flex: 0.1,
        minWidth: 150,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'customerPoNumber',
        sortable: false,
        headerName: 'Customer PO Number',
        align: 'left',
        flex: 0.12,
        minWidth: 170,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'invoiceNumber',
        sortable: false,
        headerName: 'Invoice Number',
        align: 'left',
        flex: 0.12,
        minWidth: 130,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'orderNumber',
        sortable: false,
        headerName: 'Order Number',
        align: 'left',
        flex: 0.12,
        minWidth: 130,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'description',
        sortable: false,
        headerName: 'Description',
        align: 'left',
        flex: 0.12,
        minWidth: 130,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} align="right" />,
        resizable: false,
        field: 'totalAmount',
        sortable: false,
        headerName: 'Total',
        headerAlign: 'right',
        align: 'right',
        flex: 0.12,
        minWidth: 120,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'authorizationCode',
        sortable: false,
        headerName: 'Authorization Code',
        flex: 0.12,
        minWidth: 160,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'captureType',
        sortable: false,
        headerName: 'Capture Type',
        flex: 0.12,
        minWidth: 140,
      },
      {
        resizable: false,
        field: 'createdOn',
        sortable: false,
        headerName: 'Created On',
        flex: 0.1,
        minWidth: 130,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'paidByEmail',
        sortable: false,
        headerName: 'Paid By Email',
        flex: 0.15,
        minWidth: 160,
      },
      {
        // eslint-disable-next-line react/display-name
        renderCell: (params: GridRenderCellParams) => <TruncatedTextWithTooltip value={params.value || ''} />,
        resizable: false,
        field: 'paidByName',
        sortable: false,
        headerName: 'Paid By Name',
        flex: 0.12,
        minWidth: 150,
      },
      {
        resizable: false,
        field: 'paidOn',
        sortable: false,
        headerName: 'Paid On',
        flex: 0.1,
        minWidth: 130,
      },
    ],
    [isCustomerEnabled, isCreditMemoEnabled, hasCreditApplied, isConvenienceFeeEnabled],
  );

  // A saved order predates any column added since the merchant last reordered their table, so
  // reconcile it against the current columns rather than letting the grid append them to the end.
  const orderedFields = useMemo(
    () =>
      mergeSavedColumnOrder(
        savedOrder,
        paymentColumns.map(column => column.field),
      ),
    [savedOrder, paymentColumns],
  );

  // The loading skeleton is a separate component from the real grid and has no visibility
  // into the grid's live columnVisibilityModel, so it's handed this pre-filtered/ordered list
  // instead of the full paymentColumns definition - keeping it in sync with what the user
  // actually configured via the COLUMNS picker (see handleColumnVisibilityModelChange above).
  const skeletonColumns = useMemo(
    () =>
      (orderedFields ?? paymentColumns.map(column => column.field))
        .map(field => paymentColumns.find(column => column.field === field))
        .filter((column): column is GridColDef => !!column && !column.hide && visibilityModel[column.field] !== false),
    [orderedFields, paymentColumns, visibilityModel],
  );

  // eslint-disable-next-line no-underscore-dangle
  const onRowDoubleClick = (params: GridRowParams) => handleViewPayment(params?.row?._raw as Payment);

  const onSelectionModelChange = (selectionModel: GridSelectionModel) => {
    // Enforce single selection.
    if (selectionModel.length > 2) {
      // eslint-disable-next-line no-param-reassign
      selectionModel.length = 0;
    } else if (selectionModel.length > 1) {
      // Remove previous selection.
      selectionModel.shift();
    }
    if (selectionModel.length === 1) {
      setSelectedRecord(getPaymentById(selectionModel[0] as string));
    } else {
      setSelectedRecord(undefined);
    }
  };

  const gridOptions: DataGridProProps = {
    apiRef: gridApiRef,
    columns: paymentColumns,
    rows: paymentRows,
    loading: loadingPage,
    onRowsScrollEnd: handlePageLoad,
    'aria-label': 'Payments Table',
    onRowDoubleClick,
    onSelectionModelChange,
    checkboxSelection: true,
    initialState: {
      columns: {
        orderedFields,
        columnVisibilityModel: defaultVisibilityModel,
      },
    },
    onColumnOrderChange: handleColumnOrderChange,
    onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
  };

  return (
    <DataList
      gridOptions={gridOptions}
      skeletonColumns={skeletonColumns}
      isSkeletonHidden={!isLoadingPayments}
      cypressTag="payments-table-body"
      hasCheckbox
      noDataText="No payment data to show."
    />
  );
};

export default PaymentList;
